import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { Box } from '@mui/material';
import CustomInfoIcon from './CustomInfoIcon';
import polygon from '../images/polygon.png';
import CloseIcon from '@mui/icons-material/Close';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(211, 211, 211, 0.05)',
  },
  '& .MuiPaper-root': {
    position: 'absolute',
    boxShadow: '2px 4px 25px 2px rgba(0, 0, 0, 0.25)',
    zIndex: 1300,
    borderTop: '0.125em solid silver',
    borderRight: '0.25em solid silver',
    borderBottom: '0.125em solid silver',
    borderLeft: '0.25em solid silver',
    transform: 'translate(-50%, -50%)',
  },
}));

const InfoPopupAdded = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const formatNumberForLocale = (number, locale) => {
    return new Intl.NumberFormat(locale, {
      style: 'decimal',
      minimumFractionDigits: 1,
      maximumFractionDigits: 20,
    }).format(number);
  };

  const [open, setOpen] = useState(false);
  const [dialogPosition, setDialogPosition] = useState({ top: '50%', left: '50%' });
  const parentRef = useRef(null);

  useEffect(() => {
    const updateDialogPosition = () => {
      if (parentRef.current) {
        const rect = parentRef.current.getBoundingClientRect();
        setDialogPosition({
          top: `${rect.top + window.scrollY + rect.height / 2}px`,
          left: `${45 + rect.left + window.scrollX + rect.width}px`,
        });
      }
    };

    window.addEventListener('resize', updateDialogPosition);
    return () => {
      window.removeEventListener('resize', updateDialogPosition);
    };
  }, []);

  const handleClickOpen = () => {
    setOpen(true);
    if (parentRef.current) {
      const rect = parentRef.current.getBoundingClientRect();
      setDialogPosition({
        top: `${rect.top + window.scrollY + rect.height / 2}px`,
        left: `${45 + rect.left + window.scrollX + rect.width}px`,
      });
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div ref={parentRef} style={{ position: 'relative' }}>
      <IconButton onClick={handleClickOpen} aria-label="information">
        <CustomInfoIcon />
      </IconButton>
      <StyledDialog
        open={open}
        onClose={handleClose}
        aria-labelledby="info-dialog-title"
        PaperProps={{
          style: {
            top: dialogPosition.top,
            left: dialogPosition.left,
            minWidth: '850px',
            height: '600px',
            maxWidth: '1150px',  // Ensure the dialog does not exceed the viewport width
            maxHeight: '1100px',  // Ensure the dialog does not exceed the viewport height
            overflowY: 'auto',  // Add scroll if content exceeds the height
          },
        }}
      >
        <DialogTitle id="info-dialog-title" sx={{ ml: '40px', mt: '30px', mb: '20px', fontSize: '2rem' }}>
          {t('Friction coefficient')}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" sx={{ ml: '40px' }}>
            <Box flexShrink={0}>
              <img src={polygon} alt="Info" style={{ maxHeight: '450px', width: 'auto', height: '100%' }} />
            </Box>
            <Box>
              <div style={{ display: 'flex', fontFamily: 'Arial', fontSize: '1.3rem', fontWeight: 'bold', marginTop: '-5px', marginLeft: '50px', width: '150px', gap: '10px' }}>
                <p>{formatNumberForLocale(parseFloat(('1.0').replace(',', '.')), locale)}</p>
                <p>{t('Optimal hold')}</p>
              </div>
              <div>
                <div style={{ borderBottom: '1px solid #ccc', width: '130px', marginLeft: '50px', marginTop: '50px' }} />
              </div>
              <div style={{ display: 'flex', fontFamily: 'Arial', fontSize: '1.3rem', fontWeight: 'bold', marginTop: '3px', marginLeft: '50px', width: '150px', gap: '10px' }}>
                <p>{formatNumberForLocale(parseFloat(('0.7').replace(',', '.')), locale)}</p>
                <p>{t('Rough surface')}</p>
              </div>
              <div>
                <div style={{ borderBottom: '1px solid #ccc', width: '130px', marginLeft: '50px', marginTop: '50px' }} />
              </div>
              <div style={{ display: 'flex', fontFamily: 'Arial', fontSize: '1.3rem', fontWeight: 'bold', marginTop: '3px', marginLeft: '50px', width: '150px', gap: '10px' }}>
                <p>{formatNumberForLocale(parseFloat(('0.5').replace(',', '.')), locale)}</p>
                <p>{t('Smooth, dry surface')}</p>
              </div>
              <div>
                <div style={{ borderBottom: '1px solid #ccc', width: '130px', marginLeft: '50px', marginTop: '50px' }} />
              </div>
              <div style={{ display: 'flex', fontFamily: 'Arial', fontSize: '1.3rem', fontWeight: 'bold', marginTop: '3px', marginLeft: '50px', width: '150px', gap: '10px' }}>
                <p>{formatNumberForLocale(parseFloat(('0.2').replace(',', '.')), locale)}</p>
                <p>{t('Wet surface')}</p>
              </div>
              <div>
                <div style={{ borderBottom: '1px solid #ccc', width: '130px', marginLeft: '50px', marginTop: '50px' }} />
              </div>
              <div style={{ display: 'flex', fontFamily: 'Arial', fontSize: '1.3rem', fontWeight: 'bold', marginTop: '3px', marginLeft: '50px', width: '150px', gap: '10px' }}>
                <p>{formatNumberForLocale(parseFloat(('0.1').replace(',', '.')), locale)}</p>
                <p>{t('Oily surface')}</p>
              </div>
            </Box>
            <Box marginLeft='60px'>
              <div style={{ fontSize: '1.3rem', fontFamily: 'Arial', lineHeight: '20px', width: '400px' }}>
                {t('The coefficient of friction measures the friction or resistance that occurs between the suction lip and the workpiece when they are moved relative to each other under load. It is not possible to make general statements about the values, as the surface quality of the workpiece and the characteristics of the vacuum suction cup have a significant influence on the coefficient of friction. Specific tests are required to determine this.')}
              </div>
            </Box>
          </Box>
        </DialogContent>
      </StyledDialog>
    </div>
  );
};

export default InfoPopupAdded;
